<template>
  <div class="cloud-file-browser">
    <div class="browser-header flex space-between align-center gap10">
      <h3>{{$t('selectFromCloud')}}</h3>
      <button @click="$emit('close')" class="btn clear round_">
        <div v-html="svgs.x" class="svg-parrent"></div>
      </button>
    </div>
    
    <div class="browser-content">
      <div v-if="isLoading" class="loader-wrapper flex align-center justify-center" style="min-height: 200px;">
        <MiniLoader/>
      </div>
      
      <div v-else-if="error" class="error-message">
        {{error}}
      </div>
      
      <div v-else-if="files.length === 0" class="empty-state">
        <p>{{$t('noFilesFound')}}</p>
      </div>
      
      <div v-else class="files-list">
        <div 
          v-for="file in files" 
          :key="file.key"
          class="file-item flex space-between align-center gap10"
          :class="{ 'selected': selectedFile?.key === file.key }"
          @click="selectFile(file)"
        >
          <div class="file-info flex column gap5">
            <span class="file-name">{{file.name}}</span>
            <span class="file-meta">
              <span class="file-size-label">{{$t('fileSize')}}:</span>
              <span class="file-size-value">{{formatFileSize(file.size)}}</span>
            </span>
            <span class="file-meta">
              <span class="file-date-label">{{$t('lastModified')}}:</span>
              <span class="file-date-value">{{formatDate(file.lastModified)}}</span>
            </span>
          </div>
          <div class="file-actions">
            <button 
              @click.stop="selectFile(file)" 
              class="btn primary_"
              :disabled="registering"
            >
              {{$t('select')}}
            </button>
          </div>
        </div>
      </div>
      
      <div v-if="hasMore && !isLoading" class="load-more">
        <button @click="loadMore" class="btn secondary_">
          {{$t('loadMore')}}
        </button>
      </div>
    </div>
    
    <div v-if="registering" class="registering-overlay">
      <MiniLoader/>
      <p>{{$t('registeringFile')}}</p>
    </div>
  </div>
</template>

<script>
import { listCloudFiles, registerCloudFile } from '../../services/file.service'
import { alertService } from '@/apps/common/modules/common/services/alert.service'
import MiniLoader from '../MiniLoader.vue'
import { getSvgs } from '@/assets/images/svgs'

export default {
  name: 'CloudFileBrowser',
  components: { MiniLoader },
  props: {
    organizationId: {
      type: String,
      required: true
    },
    parentData: {
      type: Object,
      required: true
    },
    sectionName: {
      type: String,
      default: 'encryptedVideos'
    },
    fieldName: {
      type: String,
      default: 'encryptedVideos'
    }
  },
  data() {
    return {
      files: [],
      isLoading: false,
      error: null,
      selectedFile: null,
      registering: false,
      continuationToken: null,
      hasMore: false,
      svgs: getSvgs()
    }
  },
  async created() {
    await this.loadFiles()
  },
  methods: {
    async loadFiles() {
      this.isLoading = true
      this.error = null
      try {
        const params = {
          limit: 50
        }
        if (this.continuationToken) {
          params.continuationToken = this.continuationToken
        }
        
        const response = await listCloudFiles(this.organizationId, params)
        if (this.continuationToken) {
          // Append to existing files
          this.files = [...this.files, ...response.files]
        } else {
          // Replace files
          this.files = response.files
        }
        this.continuationToken = response.continuationToken
        this.hasMore = response.isTruncated || false
      } catch(err) {
        console.error('Error loading cloud files:', err)
        this.error = err.message || 'Failed to load files'
        alertService.toast({ type: 'danger', msg: this.error })
      } finally {
        this.isLoading = false
      }
    },
    async loadMore() {
      if (this.continuationToken) {
        await this.loadFiles()
      }
    },
    async selectFile(file) {
      this.selectedFile = file
      this.registering = true
      try {
        const response = await registerCloudFile(this.organizationId, {
          s3Key: file.key,
          name: file.name,
          parentData: this.parentData,
          sectionName: this.sectionName,
          fieldName: this.fieldName
        })
        
        // Emit the registered file (same format as upload)
        this.$emit('file-selected', {
          fileId: response.fileId,
          name: response.name,
          type: response.type,
          src: response.previewSrc,
          status: response.status
        })
        
        // Close browser
        this.$emit('close')
      } catch(err) {
        console.error('Error registering cloud file:', err)
        alertService.toast({ 
          type: 'danger', 
          msg: err.message || 'Failed to register file' 
        })
      } finally {
        this.registering = false
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    }
  }
}
</script>

<style lang="scss" scoped>
.cloud-file-browser {
  position: relative;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  max-width: 800px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
  
  .browser-header {
    padding: 20px;
    border-bottom: 1px solid #eee;
    
    h3 {
      margin: 0;
      font-size: 18px;
    }
  }
  
  .browser-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    
    .files-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      
      .file-item {
        padding: 15px;
        border: 1px solid #ddd;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s;
        
        &:hover {
          background: #f5f5f5;
          border-color: #999;
        }
        
        &.selected {
          background: #e3f2fd;
          border-color: #2196f3;
        }
        
        .file-info {
          flex: 1;
          
          .file-name {
            font-weight: 500;
            font-size: 14px;
          }
          
          .file-size, .file-date {
            font-size: 12px;
            color: #666;
          }
        }
        
        .file-actions {
          flex-shrink: 0;
          
          .btn {
            min-width: 100px;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: 500;
          }
        }
      }
    }
    
    .empty-state {
      text-align: center;
      padding: 40px;
      color: #999;
    }
    
    .error-message {
      padding: 20px;
      background: #ffebee;
      color: #c62828;
      border-radius: 4px;
    }
    
    .load-more {
      margin-top: 20px;
      text-align: center;
    }
  }
  
  .registering-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    z-index: 10;
  }
}
</style>

